/**
 * QL-375 group 8 — Existing vs new mandate (design screen 12). A component of
 * its own (`mandate_comparison`), goal-only and advisor-only: it asks how the
 * RULE is changing, not whether the portfolio obeys it. Renders nothing while
 * the goal has one side only — no bank mandate carried in by the follow-up,
 * or no new mandate written beside it.
 *
 * Two column groups over the union of both mandates' lines: a min/max side
 * fills Min / Default / Max, a fixed side shows its weight under Default with
 * "–" either side. The proposed side is bold where a line changed, with a
 * `changed` chip after the name; a line only in the bank's mandate is muted
 * with "–" on the proposed side.
 */

import type { MandateComparisonRow, MandateComparisonSide } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { getMandateComparison } from "@/lib/api/flow-instances/mandate";
import { goalSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./mandate-comparison-widget.i18n";

type MandateComparisonWidgetProps = {
  instanceId: string;
  goalId: string;
  config?: { style?: string; title?: string; description?: string };
};

const fmt = (v: number | null | undefined) => (v == null ? "–" : `${v.toFixed(1)}%`);

/**
 * One side's cells: a min/max mandate fills Min / Default / Max, a fixed one
 * has a single Weight column — the column count follows the side's mode, so a
 * fixed-vs-fixed comparison stays narrow enough for a half-width placement.
 */
function sideCells(side: MandateComparisonSide | null, fixed: boolean): string[] {
  if (!side) return fixed ? ["–"] : ["–", "–", "–"];
  if (side.kind === "fixed") return fixed ? [fmt(side.weightPct)] : ["–", fmt(side.weightPct), "–"];
  return fixed
    ? [fmt(side.defaultPct)]
    : [fmt(side.minPct), fmt(side.defaultPct), fmt(side.maxPct)];
}

function ComparisonRow({
  row,
  existingFixed,
  proposedFixed,
  changedLabel,
  removedLabel,
}: {
  row: MandateComparisonRow;
  existingFixed: boolean;
  proposedFixed: boolean;
  changedLabel: string;
  removedLabel: string;
}) {
  const existing = sideCells(row.existing, existingFixed);
  const proposed = sideCells(row.proposed, proposedFixed);
  const removed = row.proposed === null;
  const emphasis = row.changed && !removed;
  return (
    <tr className={cn("border-t text-sm", removed && "text-muted-foreground")}>
      <td className="py-3 pr-3">
        <span className="font-medium" title={row.isin ?? undefined}>
          {row.name ?? row.isin ?? "—"}
        </span>
        {removed ? (
          <span className="ml-2 text-xs text-muted-foreground">{removedLabel}</span>
        ) : (
          row.changed && (
            <Badge variant="secondary" className="ml-2 font-normal">
              {changedLabel}
            </Badge>
          )
        )}
      </td>
      <td className="hidden @3xl:table-cell py-3 pr-3 text-muted-foreground font-mono text-xs">
        {row.isin ?? "—"}
      </td>
      {existing.map((cell, i) => (
        <td
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed three-column tuple
          key={`e${i}`}
          className="py-3 px-2 text-right tabular-nums text-muted-foreground"
        >
          {cell}
        </td>
      ))}
      {proposed.map((cell, i) => (
        <td
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed three-column tuple
          key={`p${i}`}
          className={cn(
            "py-3 px-2 text-right tabular-nums",
            emphasis ? "font-semibold" : "text-muted-foreground",
          )}
        >
          {cell}
        </td>
      ))}
    </tr>
  );
}

export function MandateComparisonWidget({
  instanceId,
  goalId,
  config,
}: MandateComparisonWidgetProps) {
  const t = useT(T);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: goalSnapshotQueryKeys.mandateComparison(goalId),
    queryFn: () => getMandateComparison(instanceId, goalId),
  });

  if (isLoading) return null;
  if (isError || !data) {
    return (
      <div className="border border-destructive/50 rounded-lg p-4 flex items-center gap-2 text-sm text-destructive">
        {t.fetchError}
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => refetch()}
        >
          {t.retry}
        </Button>
      </div>
    );
  }
  const comparison = data.comparison;
  // One side only: nothing to compare, nothing rendered.
  if (!comparison) return null;

  const componentStyle = config?.style ?? "none";
  const wrapperClass =
    componentStyle === "border"
      ? "border rounded-lg p-4"
      : componentStyle === "box"
        ? "bg-muted/30 rounded-lg p-4"
        : "";
  const existingFixed = comparison.existing.mode === "fixed";
  const proposedFixed = comparison.proposed.mode === "fixed";
  const bandHeaders = (fixed: boolean, side: "e" | "p") =>
    (fixed ? [t.colWeight] : [t.colMin, t.colDefault, t.colMax]).map((h, i) => (
      <th
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed three-column tuple
        key={`${side}${i}`}
        className="py-2 px-2 text-right font-normal"
      >
        {h}
      </th>
    ));

  return (
    <div className={cn("@container", wrapperClass)}>
      <FlowComponentHeader
        title={config?.title || t.title}
        description={config?.description || t.description}
      />
      <div className="mt-3 overflow-x-auto rounded-lg border">
        <table className="w-full border-collapse">
          <thead className="bg-muted/40 text-sm text-muted-foreground">
            <tr>
              <th />
              <th className="hidden @3xl:table-cell" />
              <th colSpan={existingFixed ? 1 : 3} className="py-2 px-2 text-center font-normal">
                {t.existingGroup}
                <span className="block text-xs">{comparison.existing.name}</span>
              </th>
              <th
                colSpan={proposedFixed ? 1 : 3}
                className="py-2 px-2 text-center font-medium text-foreground"
              >
                {t.proposedGroup}
                <span className="block text-xs font-normal">{comparison.proposed.name}</span>
              </th>
            </tr>
            <tr className="border-t">
              <th className="py-2 pr-3 pl-3 text-left font-normal">{t.colName}</th>
              <th className="hidden @3xl:table-cell py-2 pr-3 text-left font-normal">
                {t.colIsin}
              </th>
              {bandHeaders(existingFixed, "e")}
              {bandHeaders(proposedFixed, "p")}
            </tr>
          </thead>
          <tbody className="[&>tr>td:first-child]:pl-3">
            {comparison.rows.map((row) => (
              <ComparisonRow
                key={`${row.isin ?? ""}:${row.name ?? ""}`}
                row={row}
                existingFixed={existingFixed}
                proposedFixed={proposedFixed}
                changedLabel={t.changed}
                removedLabel={t.removed}
              />
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        {t.footer}
        {comparison.frozen && ` ${t.frozen}`}
      </p>
    </div>
  );
}
